import { NavLink, useLocation } from 'react-router-dom';
import { ArrowLeft, Building2 } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import type { OffOrgNavSection } from '@/config/offOrgNavigation';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useTranslation } from '@/hooks/useTranslation';

interface OffOrgNavigationProps {
  /** The sidebar's sections: `ACCOUNT_NAV_SECTIONS` or `SYSTEM_NAV_SECTIONS`. */
  sections: OffOrgNavSection[];
}

/** Active on the item's own page and on any page nested under it (`/system-settings/bedes/terms/x`). */
function isItemActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * Sidebar for pages outside the org routes — the per-user pages and System
 * Settings (docs/NAVIGATION.md "Off-org navigation"). The org sidebar builds
 * every link from the selected organization, so off the org routes — and for
 * someone with no organization at all — its links went nowhere. This one needs
 * no organization: a way back (to the selected, else most recent, else first
 * organization; the organization picker when there is none) and the sections
 * it is given, with absolute hrefs.
 */
export function OffOrgNavigation({ sections }: OffOrgNavigationProps) {
  const { t } = useTranslation('common');
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const { selectedOrganization, organizations, recentOrgIds } = useOrganization();
  const { pathname } = useLocation();

  // Off the org routes nothing is selected after a reload or a direct link, so
  // fall back to the most recently used organization, then the first one. The
  // path is built by hand: no organization is selected for useOrgBasePath()
  // (a CLAUDE.md "Org-Scoped Navigation" exception, like OrganizationSwitcher).
  const backOrg =
    selectedOrganization ??
    recentOrgIds.map((id) => organizations.find((org) => org.id === id)).find(Boolean) ??
    organizations[0] ??
    null;
  const back = backOrg
    ? { href: `/org/${backOrg.id}`, label: t('nav.account.backTo', { org: backOrg.name }), icon: ArrowLeft }
    : { href: '/org-select', label: t('nav.account.chooseOrganization'), icon: Building2 };

  const tooltip = (label: string) => (collapsed ? { children: label, side: 'right' as const } : undefined);

  return (
    <Sidebar collapsible="icon">
      <SidebarContent className="pt-2 pb-4">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={tooltip(back.label)} className="p-1.5 [&>svg]:size-5">
                  <NavLink to={back.href} end>
                    <back.icon />
                    {!collapsed && <span className="truncate">{back.label}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        {sections.map((section, index) => (
          <SidebarGroup key={section.headingKey ?? index}>
            {/* The org secondary nav's eyebrow look; hidden in the icon rail, where a heading has no room. */}
            {section.headingKey && !collapsed && (
              <SidebarGroupLabel className="font-extralight">{t(section.headingKey)}</SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const label = t(item.labelKey);
                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton
                        asChild
                        isActive={isItemActive(pathname, item.href)}
                        tooltip={tooltip(label)}
                        className="p-1.5 [&>svg]:size-5"
                      >
                        <NavLink to={item.href} end>
                          {item.icon && <item.icon />}
                          {!collapsed && <span className="truncate">{label}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}
